import { DataProvider } from "@refinedev/core";
import { IDataContextProvider } from "@refinedev/core/dist/contexts/data/IDataContext";
import { AxiosInstance } from "axios";
import axAPI from "../../../api/axios-instance";
import {
  MedicineConferencePageHeader,
  MedicineContest,
  MedicineContestFormValues,
  MedicineContestPhotoFormValues,
  MedicineContestRegulationFormValues,
  MedicineContestWinnerFormValues,
} from "../Medicine-Contests/types";

const CONTEST_TYPE = "CONTEST";
const CONTESTS_URL = "/cms/medicine/contests";
const CONFERENCE_URL = "/cms/medicine/conference";
const REGULATION_URL = `${CONTESTS_URL}/regulation`;
const PHOTO_URL = `${CONTESTS_URL}/photo`;
const WINNER_URL = `${CONTESTS_URL}/winner`;

const isConferenceContestType = (contestType?: string) =>
  contestType === "CONFERENCE";

const getUpsertUrl = (resource: string, contestType?: string) => {
  if (resource === "medicine-contest-regulations") {
    return `${REGULATION_URL}/upsert`;
  }

  if (resource === "medicine-contest-photos") {
    return `${PHOTO_URL}/upsert`;
  }

  if (resource === "medicine-contest-winners") {
    return `${WINNER_URL}/upsert`;
  }

  return `${CONTESTS_URL}/upsert`;
};

const getContestsApiData = async (
  httpClient: AxiosInstance,
  contestType?: string
) => {
  const { data } = await httpClient.get(CONTESTS_URL, {
    params: {
      contestType: contestType || CONTEST_TYPE,
    },
  });

  return {
    conference: null,
    contests: (data?.contests || []) as MedicineContest[],
  };
};

const getConferencePageUpsertPayload = (
  payload: MedicineConferencePageHeader
) => {
  const { id: _id, bannerImageUrl, htmlText } = payload;

  return {
    bannerImageUrl: bannerImageUrl || null,
    htmlText: htmlText || null,
  };
};

export const medicineContestsDataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axAPI
): DataProvider =>
  <IDataContextProvider>{
    custom: async ({ url, meta }) => {
      try {
        if (url === "seasons") {
          const { leagueId } = (meta || {}) as { leagueId?: number };

          if (!leagueId) {
            return { data: [] };
          }

          // Справочный запрос для фильтра. При ошибке отдаём пустой список,
          // чтобы не уронить страницу рендером объекта-ошибки в нотификации.
          try {
            const { data } = await httpClient.get("cms/info/seasonHistory", {
              params: { leagueId },
            });

            return { data: data || [] };
          } catch {
            return { data: [] };
          }
        }

        if (url === "teams") {
          const { leagueId, seasonId: metaSeasonId } = (meta || {}) as {
            leagueId?: number;
            seasonId?: number;
          };

          if (!leagueId) {
            return { data: [] };
          }

          try {
            let seasonId = metaSeasonId;

            if (!seasonId) {
              const { data: seasons } = await httpClient.get(
                `cms/info/allActiveSeason?leagueId=${leagueId}`
              );
              seasonId = seasons?.[0]?.seasonId;
            }

            if (!seasonId) {
              return { data: [] };
            }

            const { data } = await httpClient.get("cms/info/teamsBySeason", {
              params: { leagueId, seasonId },
            });

            return { data };
          } catch {
            return { data: [] };
          }
        }

        return Promise.reject(new Error("Неизвестный запрос"));
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    getList: async ({ resource, meta }) => {
      try {
        if (resource === "medicine-contest-years") {
          const contestType = meta?.contestType || CONTEST_TYPE;
          const { data } = await httpClient.get(`${CONTESTS_URL}/years`, {
            params: {
              contestType,
            },
          });
          const years = data?.years || [];

          return {
            data: years.map((year: number) => ({ id: year, year })),
            total: years.length,
          };
        }

        if (resource === "medicine-contest-years-with-items") {
          const contestType = meta?.contestType || CONTEST_TYPE;
          const { data } = await httpClient.get(`${CONTESTS_URL}/years`, {
            params: {
              contestType,
            },
          });
          const years = data?.years || [];
          const yearItems = await Promise.all(
            years.map(async (year: number) => {
              const { data } = await httpClient.get(CONTESTS_URL, {
                params: {
                  year,
                  contestType,
                },
              });
              const contests: MedicineContest[] = data?.contests || [];

              return {
                id: year,
                year,
                contest: contests[0],
              };
            })
          );

          return {
            data: yearItems,
            total: yearItems.length,
          };
        }

        const contestType = meta?.contestType || CONTEST_TYPE;
        const { data } = await httpClient.get(CONTESTS_URL, {
          params: {
            year: meta?.year,
            contestType,
          },
        });
        const contests = data?.contests || [];

        return {
          data: contests,
          total: contests.length,
        };
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    getOne: async ({ resource, id, meta }) => {
      try {
        if (resource === "medicine-conference") {
          const { data } = await httpClient.get(CONFERENCE_URL);

          return {
            data: data?.conference || data || null,
          };
        }

        const contestType = meta?.contestType || CONTEST_TYPE;
        const { contests } = await getContestsApiData(httpClient, contestType);
        const contest = contests.find((item) => item.id === Number(id));

        if (!contest) {
          return Promise.reject(new Error("Запись не найдена"));
        }

        return {
          data: contest,
        };
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    create: async ({ resource, variables }) => {
      try {
        if (resource === "medicine-conference") {
          const payload = getConferencePageUpsertPayload(
            variables as MedicineConferencePageHeader
          );
          const { data } = await httpClient.post(
            `${CONFERENCE_URL}/upsert`,
            payload
          );

          return {
            data: {
              ...(variables as MedicineConferencePageHeader),
              id:
                Number(data) || (variables as MedicineConferencePageHeader)?.id,
            },
          };
        }

        const contestType = (variables as MedicineContestFormValues)
          ?.contestType;
        const url = getUpsertUrl(resource, contestType);
        const { id, ...restVariables } = variables as { id?: number | null };
        const payload = id ? { ...restVariables, id } : restVariables;
        const { data } = await httpClient.post(url, payload);

        if (resource === "medicine-contests" && !data?.id) {
          const contestPayload = payload as MedicineContestFormValues;
          const { contests } = await getContestsApiData(
            httpClient,
            contestPayload.contestType
          );
          const createdContest = contests.find(
            (contest) =>
              contest.year === contestPayload.year &&
              contest.title === contestPayload.title
          );

          if (createdContest) {
            return {
              data: createdContest,
            };
          }
        }

        return {
          data,
        };
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    update: async ({
      resource,
      variables,
    }:
      | {
          id: string;
          resource: string;
          variables:
            | MedicineContestFormValues
            | MedicineConferencePageHeader
            | MedicineContestRegulationFormValues;
        }
      | {
          id: string;
          resource: string;
          variables: MedicineContestPhotoFormValues;
        }
      | {
          id: string;
          resource: string;
          variables: MedicineContestWinnerFormValues;
        }) => {
      try {
        if (resource === "medicine-conference") {
          const payload = getConferencePageUpsertPayload(
            variables as MedicineConferencePageHeader
          );
          const { data } = await httpClient.post(
            `${CONFERENCE_URL}/upsert`,
            payload
          );

          return {
            data: {
              ...(variables as MedicineConferencePageHeader),
              id:
                Number(data) || (variables as MedicineConferencePageHeader)?.id,
            },
          };
        }

        const contestType = (variables as MedicineContestFormValues)
          ?.contestType;
        const url = getUpsertUrl(resource, contestType);
        const { data } = await httpClient.post(url, variables);

        return {
          data: data?.id ? data : variables,
        };
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    deleteOne: async ({ resource, id, meta }) => {
      try {
        if (resource === "medicine-conference") {
          const { data } = await httpClient.post(`${CONFERENCE_URL}/remove`);

          return {
            data,
          };
        }

        let url = `${CONTESTS_URL}/remove/${id}`;

        if (resource === "medicine-contest-regulations") {
          url = `${REGULATION_URL}/remove/${id}`;
        }

        if (resource === "medicine-contest-photos") {
          url = `${PHOTO_URL}/remove/${id}`;
        }

        if (resource === "medicine-contest-winners") {
          url = `${WINNER_URL}/remove/${id}`;
        }

        const { data } = await httpClient.post(url);

        return {
          data,
        };
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
  };
